import {
  FC,
  UIEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Flex, TabPanel, TabPanels } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import useRect from 'common/hooks/useRect';
import useResize from 'common/hooks/useResize';
import throttleToNextFrame from 'common/throttleToNextFrame';
import Accordion from 'components/_core/Accordion';
import Callout from 'components/_core/Callout';
import { logEvent } from 'features/Analytics/analytics';
import {
  MOBILE_HEADER_HEIGHT,
  TopBar,
} from 'features/Onboarding/modules/StepsPricingPlan/components/StepsLayout';

import ReferralOffer from '../../../../components/ReferralOffer';
import { OnboardingPlanDisplay } from '../../types';
import PlanCard from './components/PlanCard';
import PlanTabList from './components/PlanTabList';
import PlanTabs from './components/PlanTabs';
import StickyMobileButton from './components/StickyMobileButton';
import locales from './locales';

const PRICING_PAGE_URL = 'https://www.shine.fr/tarifs/';

const STICKY_THRESHOLD = 5;

interface PlansMobileProps {
  onChoosePlanDisplay: (plan: OnboardingPlanDisplay) => void;
  referrer?: string;
  planDisplays: OnboardingPlanDisplay[];
  onPrev: () => void;
}

const PlansMobile: FC<PlansMobileProps> = ({
  onChoosePlanDisplay,
  planDisplays,
  referrer,
}) => {
  const showStickyRef = useRef(false);
  const [showSticky, setShowSticky] = useState(false);
  const initialized = useRef(false);
  const windowWidth = useRef(0);
  const windowHeight = useRef(0);
  const progressRef = useRef<HTMLDivElement>(null);
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const docHeight = useRef(0);
  const rootContainer = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);

  const [{ height: stickyHeight }] = useRect(stickyContainerRef);

  const markerRef = useRef<HTMLDivElement>(null);
  const previousTab = useRef<number | null>(null);
  const [currentTab, setCurrentTab] = useState(1); // set Plus plan as default tab
  const currentTabRef = useRef(currentTab);

  const nbPlans = planDisplays.length;

  const previousPlan =
    previousTab.current !== null
      ? planDisplays[previousTab.current]?.providerPlanId
      : null;

  const newPlan = planDisplays[currentTab]?.providerPlanId;

  const selectedPlan = planDisplays[currentTab];

  useResize(() => {
    windowWidth.current = window.innerWidth;
    windowHeight.current = window.innerHeight;
    const rect = rootContainer.current?.getBoundingClientRect();
    docHeight.current = rect?.height ?? 0;

    // move the scrollable to the default tab
    if (!initialized.current) {
      initialized.current = true;
      const left = currentTab * windowWidth.current;
      container.current?.scrollTo({ behavior: 'auto', left });
    }
  });

  useEffect(() => {
    if (previousPlan && previousPlan !== newPlan) {
      logEvent({
        name: 'Price Plan Switched',
        properties: {
          from: previousPlan,
          to: newPlan,
        },
      });
    }
    previousTab.current = currentTab;
  }, [currentTab, previousPlan, newPlan]);

  const handleTabsChange = (index: number) => {
    const left = index * windowWidth.current;
    container.current?.scrollTo({ behavior: 'smooth', left });
  };

  const handleScrollX: UIEventHandler<HTMLDivElement> = throttleToNextFrame(
    () => {
      const scrollContainer = container.current;

      if (!scrollContainer) {
        return;
      }

      const markerElement = markerRef.current;

      if (!markerElement) {
        return;
      }

      const x = scrollContainer?.scrollLeft ?? 0;
      const current = x / windowWidth.current;
      const xPercent = current * 100;

      // using ref instead of state to spare rerenders as it's running on a mobile at every frame
      markerElement.style.transform = `translateX(${xPercent}%)`;

      const index = Math.round(current);
      if (index !== currentTabRef.current) {
        currentTabRef.current = index;
        setCurrentTab(index);
      }
    },
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScrollY = useCallback(
    throttleToNextFrame(() => {
      const { scrollY } = window;

      const yPercent = Math.trunc(
        (scrollY /
          (docHeight.current + MOBILE_HEADER_HEIGHT - windowHeight.current)) *
          100,
      );

      if (yPercent < STICKY_THRESHOLD) {
        if (showStickyRef.current) {
          showStickyRef.current = false;
          setShowSticky(false);
        }
      } else if (!showStickyRef.current) {
        showStickyRef.current = true;
        setShowSticky(true);
      }

      const progressElement = progressRef.current;

      if (!progressElement) {
        return;
      }

      // using ref instead of state to spare rerenders as it's running on a mobile at every frame
      progressElement.style.transform = `scaleX(${yPercent}%)`;
    }),
    [],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScrollY);

    return () => {
      window.removeEventListener('scroll', handleScrollY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!selectedPlan) {
    throw new Error('No plan selected');
  }

  return (
    <Box isolation="isolate" paddingBottom={stickyHeight} ref={rootContainer}>
      <TopBar.Title>{locales.title}</TopBar.Title>
      <Flex
        alignItems="center"
        background="white"
        flexDir="column"
        position="relative"
        zIndex={2}
      >
        {referrer ? <ReferralOffer referrer={referrer} /> : null}
        <PlanTabs currentTab={currentTab} onChange={handleTabsChange}>
          <PlanTabList markerRef={markerRef} plans={planDisplays} />
          <Box
            isolation="isolate"
            onScroll={handleScrollX}
            overflow="scroll"
            ref={container}
            scrollSnapType="x mandatory"
            width="100vw"
          >
            <Flex as={TabPanels} direction="row" width={`${nbPlans * 100}vw`}>
              {planDisplays.map((plan) => (
                <TabPanel
                  display="block!important"
                  key={plan.providerPlanId}
                  scrollSnapAlign="start"
                  width="100vw"
                >
                  <PlanCard onChoose={onChoosePlanDisplay} plan={plan} />
                </TabPanel>
              ))}
            </Flex>
          </Box>
        </PlanTabs>
        <Box marginBottom="space-40" paddingX="space-16" width="100%">
          <Callout>
            <Callout.Content> {locales.details.mobileLine1}</Callout.Content>

            <Callout.Button as="a" href={PRICING_PAGE_URL} target="_blank">
              {locales.details.mobileLine2}
            </Callout.Button>
          </Callout>
        </Box>
        <Box marginBottom="space-32" paddingX="space-16" width="100%">
          <Accordion>
            {locales.questions.map(({ content, title }) => (
              <Accordion.Item key={title} title={title}>
                <Accordion.Text>{content}</Accordion.Text>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>
      </Flex>
      <Box
        animate={{
          transition: showSticky
            ? {
                duration: 0.25,
                type: 'ease-out',
              }
            : {
                duration: 0.2,
                type: 'ease-in',
              },
          y: showSticky ? 0 : '100%',
        }}
        as={motion.div}
        bottom="0"
        initial={{
          y: '100%',
        }}
        position="fixed"
        ref={stickyContainerRef}
        width="100%"
        zIndex="3"
      >
        <StickyMobileButton
          onChoose={onChoosePlanDisplay}
          plan={selectedPlan}
          progressRef={progressRef}
        />
      </Box>
    </Box>
  );
};

export default PlansMobile;
